export const DEFAULT_COMPENSATION_CONFIG = {
  trackingDollarsPerSecond: 0.05,
  basePay: 1.00,
  baseBonus: 2.25,
  queueTimeoutPay: 0.75,
  playerCount: 2,
  stage1DurationSeconds: 90,
  stage2DurationSeconds: 135,
};

export const PAYOFF_FRAME_GAIN = "gain";
export const PAYOFF_FRAME_LOSS = "loss";
export const NEGOTIATION_TERM_OFFER = "offer";
export const NEGOTIATION_TERM_DEMAND = "demand";
export const COUNTERPART_TERM_PARTNER = "partner";
export const COUNTERPART_TERM_OPPONENT = "opponent";

export const EXPECTED_COMBINED_IN_CIRCLE_RATE = 0.4;
export const REALISTIC_MAX_COMBINED_IN_CIRCLE_RATE = 0.6;
export const REALISTIC_MAX_STAGE2_SHARE = 0.75;

export function numericCompensationValue(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function normalizePayoffFrame(value) {
  return value === PAYOFF_FRAME_LOSS ? PAYOFF_FRAME_LOSS : PAYOFF_FRAME_GAIN;
}

export function normalizeNegotiationTerm(value) {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalized === NEGOTIATION_TERM_DEMAND
    ? NEGOTIATION_TERM_DEMAND
    : NEGOTIATION_TERM_OFFER;
}

export function isLossFrame(value) {
  return normalizePayoffFrame(value) === PAYOFF_FRAME_LOSS;
}

// An unset or unknown counterpart term follows the payoff frame, which is how
// games created before the counterpartTerm factor existed were worded.
export function normalizeCounterpartTerm(value, payoffFrame) {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (
    normalized === COUNTERPART_TERM_PARTNER ||
    normalized === COUNTERPART_TERM_OPPONENT
  ) {
    return normalized;
  }
  return isLossFrame(payoffFrame)
    ? COUNTERPART_TERM_OPPONENT
    : COUNTERPART_TERM_PARTNER;
}

export function clampPayoffDollars(value, maxDollars) {
  const number = Number(value);
  const maxNumber = Number(maxDollars);
  const safeValue = Number.isFinite(number) ? number : 0;
  const safeMax = Number.isFinite(maxNumber) ? Math.max(0, maxNumber) : Infinity;

  return Math.max(0, Math.min(safeValue, safeMax));
}

export function roundDollarsToCents(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const dollars = Number(value);
  if (!Number.isFinite(dollars)) {
    return null;
  }

  const scaled = dollars * 100;
  const floatingPointTolerance =
    Number.EPSILON * Math.max(1, Math.abs(scaled)) * 2;
  return (
    Math.round(scaled + Math.sign(scaled) * floatingPointTolerance) / 100
  );
}

export function splitDollarsAsEvenlyAsPossible(value) {
  const dollars = roundDollarsToCents(value);
  if (dollars === null || dollars < 0) {
    return null;
  }

  const totalCents = Math.round(dollars * 100);
  const firstShareCents = Math.ceil(totalCents / 2);
  return [firstShareCents / 100, (totalCents - firstShareCents) / 100];
}

export function demandFromLossOffer(lossOfferDollars, stakeDollars) {
  const stake = clampPayoffDollars(stakeDollars, Infinity);
  return clampPayoffDollars(stake - clampPayoffDollars(lossOfferDollars, stake), stake);
}

export function lossOfferFromDemand(demandDollars, stakeDollars) {
  const stake = clampPayoffDollars(stakeDollars, Infinity);
  return clampPayoffDollars(stake - clampPayoffDollars(demandDollars, stake), stake);
}

export function resolveCompensationConfig({ treatment = {}, design = {} } = {}) {
  const playerCount = numericCompensationValue(
    design.playerCount ?? treatment.playerCount,
    DEFAULT_COMPENSATION_CONFIG.playerCount
  );
  const trackingDollarsPerSecond = numericCompensationValue(
    design.trackingDollarsPerSecond ??
      treatment.trackingDollarsPerSecond ??
      treatment.trackingDollarPerSecond,
    DEFAULT_COMPENSATION_CONFIG.trackingDollarsPerSecond
  );
  const stage1DurationSeconds = numericCompensationValue(
    design.stage1DurationSeconds ?? treatment.stage1DurationSeconds,
    DEFAULT_COMPENSATION_CONFIG.stage1DurationSeconds
  );
  const stage2DurationSeconds = numericCompensationValue(
    design.stage2DurationSeconds ?? treatment.stage2DurationSeconds,
    DEFAULT_COMPENSATION_CONFIG.stage2DurationSeconds
  );
  const basePay = numericCompensationValue(
    design.basePay ??
      design.showUpPayment ??
      treatment.basePay ??
      treatment.showUpPayment,
    DEFAULT_COMPENSATION_CONFIG.basePay
  );
  const baseBonus = numericCompensationValue(
    design.baseBonus ?? treatment.baseBonus,
    DEFAULT_COMPENSATION_CONFIG.baseBonus
  );
  const queueTimeoutPay = numericCompensationValue(
    design.queueTimeoutPay ?? treatment.queueTimeoutPay,
    DEFAULT_COMPENSATION_CONFIG.queueTimeoutPay
  );
  const stage1MaxPerParticipant = numericCompensationValue(
    design.stage1MaxPerParticipant ?? treatment.stage1MaxPerParticipant,
    stage1DurationSeconds * trackingDollarsPerSecond
  );
  const stage2MaxPerParticipant = numericCompensationValue(
    design.stage2MaxPerParticipant ?? treatment.stage2MaxPerParticipant,
    stage2DurationSeconds * trackingDollarsPerSecond
  );
  const stage1PooledCap = numericCompensationValue(
    design.stage1PooledCap ?? treatment.stage1PooledCap,
    stage1DurationSeconds * trackingDollarsPerSecond * playerCount
  );
  const stage2PooledCap = numericCompensationValue(
    design.stage2PooledCap ?? treatment.stage2PooledCap,
    stage2DurationSeconds * trackingDollarsPerSecond * playerCount
  );

  return {
    trackingDollarsPerSecond,
    playerCount,
    stage1DurationSeconds,
    stage2DurationSeconds,
    stage1MaxPerParticipant,
    stage2MaxPerParticipant,
    stage1PooledCap,
    stage2PooledCap,
    basePay,
    baseBonus,
    queueTimeoutPay,
    showUpPayment: basePay,
  };
}

export function calculateTotalPay({
  basePay,
  baseBonus,
  stage1Earnings = 0,
  stage2Earnings = 0,
}) {
  const safeBasePay = numericCompensationValue(
    basePay,
    DEFAULT_COMPENSATION_CONFIG.basePay
  );
  const safeBaseBonus = numericCompensationValue(
    baseBonus,
    DEFAULT_COMPENSATION_CONFIG.baseBonus
  );
  const bonus =
    numericCompensationValue(stage1Earnings, 0) +
    numericCompensationValue(stage2Earnings, 0);

  return safeBasePay + Math.max(bonus, safeBaseBonus);
}

export function calculateMinimumPay(compensationConfig) {
  return roundDollarsToCents(
    calculateTotalPay({
      ...compensationConfig,
      stage1Earnings: 0,
      stage2Earnings: 0,
    })
  );
}

export function calculateAdditionalPay(finalPay, minimumPay) {
  const finalPayDollars = roundDollarsToCents(finalPay);
  const minimumPayDollars = roundDollarsToCents(minimumPay);

  if (finalPayDollars === null || minimumPayDollars === null) {
    return null;
  }

  return roundDollarsToCents(
    Math.max(0, finalPayDollars - minimumPayDollars)
  );
}

function roundToTenths(value) {
  return Math.round(value * 10) / 10;
}

export function calculateExpectedBonusPay(
  compensationConfig,
  combinedInCircleRate = EXPECTED_COMBINED_IN_CIRCLE_RATE
) {
  const rate = Math.max(0, Number(combinedInCircleRate) || 0);
  const expectedStage1Pot = Math.min(
    compensationConfig.stage1PooledCap,
    compensationConfig.stage1DurationSeconds *
      compensationConfig.playerCount *
      compensationConfig.trackingDollarsPerSecond *
      rate
  );
  const expectedStage2Pot = Math.min(
    compensationConfig.stage2PooledCap,
    compensationConfig.stage2DurationSeconds *
      compensationConfig.playerCount *
      compensationConfig.trackingDollarsPerSecond *
      rate
  );
  const expectedBonusPay = expectedStage1Pot / 2 + expectedStage2Pot / 2;

  return {
    expectedStage1Pot,
    expectedStage2Pot,
    expectedBonusPay,
    expectedTotalPay: calculateTotalPay({
      basePay: compensationConfig.basePay,
      baseBonus: compensationConfig.baseBonus,
      stage1Earnings: expectedBonusPay,
    }),
  };
}

export function calculateRealisticMaxPay(
  compensationConfig,
  combinedInCircleRate = REALISTIC_MAX_COMBINED_IN_CIRCLE_RATE
) {
  const rate = Math.max(0, Number(combinedInCircleRate) || 0);
  const realisticStage1Pot = Math.min(
    compensationConfig.stage1PooledCap,
    compensationConfig.stage1DurationSeconds *
      compensationConfig.playerCount *
      compensationConfig.trackingDollarsPerSecond *
      rate
  );
  const realisticStage2Pot = Math.min(
    compensationConfig.stage2PooledCap,
    compensationConfig.stage2DurationSeconds *
      compensationConfig.playerCount *
      compensationConfig.trackingDollarsPerSecond *
      rate
  );
  const realisticBonusPay =
    realisticStage1Pot + realisticStage2Pot * REALISTIC_MAX_STAGE2_SHARE;

  return {
    realisticStage1Pot,
    realisticStage2Pot,
    realisticBonusPay,
    expectedMaxPay: roundToTenths(
      calculateTotalPay({
        basePay: compensationConfig.basePay,
        baseBonus: compensationConfig.baseBonus,
        stage1Earnings: realisticBonusPay,
      })
    ),
  };
}
